/**
 * useZodForm - react-hook-form + Zod 통합 커스텀 훅
 *
 * react-hook-form과 Zod를 통합하는 보일러플레이트 코드를 추상화한 커스텀 훅입니다.
 * 모든 폼에서 일관된 검증 패턴을 사용할 수 있도록 합니다.
 *
 * @example
 * ```tsx
 * import { useZodForm } from '@/lib/hooks/useZodForm';
 * import { loginSchema, type LoginFormData } from '@/lib/validations';
 *
 * function LoginForm() {
 *   const { register, handleSubmit, formState: { errors } } = useZodForm(
 *     loginSchema,
 *     { email: '', password: '' }
 *   );
 *
 *   const onSubmit = (data: LoginFormData) => {
 *     // data는 이미 검증됨
 *     console.log(data);
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit(onSubmit)}>
 *       <input {...register('email')} />
 *       {errors.email && <p>{errors.email.message}</p>}
 *     </form>
 *   );
 * }
 * ```
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type UseFormReturn, type UseFormProps, type FieldValues } from 'react-hook-form';
import type { z } from 'zod';

/**
 * react-hook-form + Zod 통합 훅
 *
 * @template TSchema - Zod 스키마 타입
 * @param schema - Zod 검증 스키마
 * @param defaultValues - 폼 기본값
 * @param options - react-hook-form 추가 옵션 (resolver, defaultValues 제외)
 * @returns react-hook-form의 UseFormReturn 객체
 */
export function useZodForm<TSchema extends z.ZodType<FieldValues>>(
  schema: TSchema,
  defaultValues: z.infer<TSchema>,
  options?: Omit<UseFormProps<z.infer<TSchema>>, 'resolver' | 'defaultValues'>
): UseFormReturn<z.infer<TSchema>> {
  /**
   * Type assertion 사용 이유:
   * - Zod v4와 @hookform/resolvers/zod 간 타입 호환성 이슈
   * - zodResolver의 제네릭 타입과 useForm 제네릭이 완전히 일치하지 않음
   * - 런타임에서는 정상 동작하며, 반환 타입으로 타입 안전성 보장
   */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  return useForm({
    resolver: zodResolver(schema as any),
    defaultValues: defaultValues as any,
    ...options,
  }) as UseFormReturn<z.infer<TSchema>>;
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

/**
 * useZodForm 반환 타입
 *
 * @template TSchema - Zod 스키마 타입
 */
export type ZodFormReturn<TSchema extends z.ZodType<FieldValues>> = UseFormReturn<z.infer<TSchema>>;
